import type { PublicMachineRoute } from "@/transport/fleet"
import type { RouteComparisonRound } from "./route-comparison-controller"

type Provider = PublicMachineRoute["provider"]

/** A route is ranked by latency only after this many successful samples in the window. */
const routeLatencyMinimumSamples = 3
// A held route is kept unless another is faster by more than this much, so
// measurement noise between similar routes does not move the connection.
const switchMarginMs = 10
const switchMarginRatio = 0.2

export type RouteLatency = {
  /** Median of the window's successful samples; undefined when none succeeded. */
  median?: number
  samples: number
  /** The latest attempt failed, with its reason. */
  failure?: RouteComparisonRound["attempts"][number]["reason"] | "unavailable"
}

/** Summarizes a provider's samples in the window, or undefined when it was never measured. */
export function routeLatency(rounds: readonly RouteComparisonRound[], provider: Provider): RouteLatency | undefined {
  const attempts = rounds.flatMap(round => round.attempts.filter(attempt => attempt.provider === provider))
  const latest = attempts.at(-1)
  if (!latest) return undefined
  const timings = attempts.filter(attempt => attempt.result === "success").map(attempt => attempt.latency_ms).sort((a, b) => a - b)
  const middle = timings.length >> 1
  const median = !timings.length ? undefined
    : timings.length % 2 ? timings[middle] : Math.round((timings[middle - 1] + timings[middle]) / 2)
  return {
    median,
    samples: timings.length,
    ...(latest.result === "success" ? {} : { failure: latest.reason ?? "unavailable" }),
  }
}

/**
 * The provider with the lowest median latency among those whose latest check
 * succeeded and that have enough samples, or undefined when none qualifies.
 * `held` stays chosen unless another is faster by a clear margin; ties go to
 * the earlier provider, which is the control plane's preference order.
 */
export function fastestProvider(rounds: readonly RouteComparisonRound[], providers: readonly Provider[], held?: Provider) {
  const ranked = providers.flatMap(provider => {
    const latency = routeLatency(rounds, provider)
    return latency?.median !== undefined && !latency.failure && latency.samples >= routeLatencyMinimumSamples
      ? [{ provider, median: latency.median }] : []
  })
  const best = ranked.reduce<typeof ranked[number] | undefined>((best, next) => !best || next.median < best.median ? next : best, undefined)
  const kept = ranked.find(entry => entry.provider === held)
  if (best && kept && kept.median - best.median <= Math.max(switchMarginMs, kept.median * switchMarginRatio)) return kept.provider
  return best?.provider
}

/**
 * The public route this browser should prefer: the user's pinned provider
 * when the machine still advertises it, otherwise the fastest one.
 */
export function preferredPublicRoute(machine: { public_routes?: PublicMachineRoute[] }, options: {
  pinned?: string
  fastest?: string
}): PublicMachineRoute | undefined {
  const routes = machine.public_routes ?? []
  return routes.find(route => route.provider === options.pinned) ?? routes.find(route => route.provider === options.fastest)
}
